import { useRef, useEffect } from 'react';
import { logger } from '@i-ark/utils';
import { throttleToAnimationFrame } from '@utils/common';

const editors = new Map();
const getRichTextEditor = editorId => {
  return editors.get(editorId);
};

// keep track of whether or not input events has been triggered before (in
// which case, we can safely assume that it's consistently supported by the
// currently used browser, and therefore disregard the keydown event from
// that point forwards)
let wasInputEventTriggered = false;
const useRichTextEditor = ({
  entryKey,
  editorId,
  tabIndex,
  maxLength,
  placeholder,
  value,
  ref,
  onChange,
  onReady,
  onFocus,
  onBlur
}) => {
  const onChangeRef = useRef(onChange);
  const onFocusRef = useRef(onFocus);
  const onBlurRef = useRef(onBlur);
  useEffect(() => {
    onFocusRef.current = onFocus;
  }, [onFocus]);
  useEffect(() => {
    onBlurRef.current = onBlur;
  }, [onBlur]);
  useEffect(() => {
    onChangeRef.current = onChange;
  }, [onChange]);
  const wrappedOnChange = value => {
    // In case gPolly is currently reading the input out loud, then don't bother
    // autosaving (since the onChange event most likely is triggered by gPolly)
    if (value.includes('data-g-polly="true"')) {
      return;
    }
    onChangeRef.current(value);
  };
  useEffect(() => {
    const node = ref.current;
    if (node === null || typeof window.gEditor === "undefined" || node.parentElement?.querySelector(".fr-box")) {
      return;
    }
    node.id = `richtexteditor-${editorId.replaceAll(":", "_")}`;
    let prevHtml;
    const editorPromise = window.gEditor(`#${node.id}`, {
      disabledPlugins: ["table"],
      useNativeEditor: true,
      toolbarButtons: [],
      toolbarButtonsXS: [],
      toolbarButtonsSM: [],
      toolbarButtonsMD: [],
      tabIndex,
      placeholderText: placeholder,
      charCounterMax: maxLength ?? -1,
      events: {
        blur() {
          onBlurRef.current?.();
          const nextHtml = this.html.get();
          if (nextHtml !== prevHtml) {
            wrappedOnChange(nextHtml);
            prevHtml = this.html.get();
          }
        },
        keydown() {
          if (!wasInputEventTriggered) {
            wrappedOnChange(this.html.get());
          }
        },
        input() {
          wasInputEventTriggered = true;
          const nextHtml = this.html.get();
          if (nextHtml !== prevHtml) {
            wrappedOnChange(nextHtml);
            prevHtml = this.html.get();
          }
        },
        contentChanged() {
          const nextHtml = this.html.get();
          if (nextHtml !== prevHtml) {
            wrappedOnChange(nextHtml);
            prevHtml = this.html.get();
          }
        },
        focus() {
          onFocusRef.current?.();
        },
        "commands.after"() {
          wrappedOnChange(this.html.get());
        }
      }
    });
    editorPromise.then(editor => {
      editors.set(editorId, editor);
      onReady();
    }, err => logger.error(err));
    const handleResize = throttleToAnimationFrame(() => {
      editorPromise.then(editor => {
        editor.placeholder?.refresh();
      }, err => logger.error(err));
    });
    window.addEventListener("resize", handleResize);
    return () => {
      editorPromise.then(editor => {
        editor.destroy();
        editors.delete(editorId);
      }, err => logger.error(err));
      window.removeEventListener("resize", handleResize);
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  // auto-update applied value whenever entryId is changing
  useEffect(() => {
    const editor = editors.get(editorId);
    if (editor === undefined || editor.html === undefined) {
      return;
    }
    if (value !== editor.html.get()) {
      editor.html.set(value);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [entryKey, value]);
};

export { getRichTextEditor, useRichTextEditor };
//# sourceMappingURL=useRichTextEditor.js.map
